/**
 * ShopOrdersView - Orders list view for Shop administration
 * Extracted from AdminShopTab.js in Phase 4D-7
 * 
 * Displays:
 * - Order list in table format
 * - Order ID (truncated), customer info, items count, total, status, date
 * - View button to open order detail dialog
 * - Empty state when no orders
 */
import React from 'react';
import { ShoppingCart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { formatCurrency, formatDateTimeShort } from '../../../utils/format';

/**
 * Get badge variant based on fulfillment status
 * @param {string} status - Fulfillment status
 * @returns {string} Badge variant
 */
function getStatusBadgeVariant(status) {
  switch (status) {
    case 'delivered':
    case 'collected':
      return 'default';
    case 'shipped':
    case 'ready_for_collection':
      return 'secondary';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
}

const STATUS_LABELS = {
  pending: 'Pending',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  ready_for_collection: 'Ready for Collection',
  collected: 'Collected',
  cancelled: 'Cancelled',
};

function getStatusLabel(status) {
  return STATUS_LABELS[status] || status;
}

/**
 * @param {Object} props
 * @param {Array} props.orders - Array of order objects
 * @param {string} props.orders[].order_id - Order ID
 * @param {string} props.orders[].customer_name - Customer name
 * @param {string} props.orders[].customer_email - Customer email
 * @param {Array} props.orders[].items - Line items
 * @param {number} props.orders[].total_amount - Order total
 * @param {string} props.orders[].fulfillment_status - Current status
 * @param {string} props.orders[].created_at - Order date (ISO string)
 * @param {Function} props.onViewOrder - Callback when View button clicked (receives order)
 */
export function ShopOrdersView({ orders, onViewOrder }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Orders ({orders.length})</CardTitle>
        <CardDescription>Manage customer orders and fulfillment</CardDescription>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No orders yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell className="font-mono text-sm">
                      {order.order_id.slice(0, 12)}...
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-xs text-slate-500">{order.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{order.items?.length || 0} items</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(order.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(order.fulfillment_status)}>
                        {getStatusLabel(order.fulfillment_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDateTimeShort(order.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewOrder(order)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ShopOrdersView;
